import { useQuery } from '@tanstack/react-query';
import { selectedWalletSelector } from '../../../store/wallet/selectors';
import { useSelector } from 'react-redux';
import { get } from '../../../lib/api/rest';

export const useUserBalances = (
  stakingTokenAddress: string | null,
  rewardsTokenAddress: string | null,
  chainId: number
) => {
  const { address: userAddress } = useSelector(selectedWalletSelector);

  // Fetch user staking token balance
  const {
    data: userStakingBalance,
    isLoading: userStakingBalanceLoading,
    error: userStakingBalanceError,
  } = useQuery({
    queryKey: [
      'userStakingBalance',
      stakingTokenAddress,
      userAddress,
      chainId,
    ],
    queryFn: async () => {
      if (!stakingTokenAddress || !userAddress) return null;

      const res = await get(
        `/api/wallet/${userAddress}/token/${stakingTokenAddress}/balance?chainId=${chainId}`
      ).send();

      if (!res.ok) {
        throw new Error('Failed to fetch staking token balance');
      }

      return res.body.balance;
    },
    enabled: !!stakingTokenAddress && !!userAddress && !!chainId,
  });

  // Fetch user rewards token balance
  const {
    data: userRewardsBalance,
    isLoading: userRewardsBalanceLoading,
    error: userRewardsBalanceError,
  } = useQuery({
    queryKey: [
      'userRewardsBalance',
      rewardsTokenAddress,
      userAddress,
      chainId,
    ],
    queryFn: async () => {
      if (!rewardsTokenAddress || !userAddress) return null;

      const res = await get(
        `/api/wallet/${userAddress}/token/${rewardsTokenAddress}/balance?chainId=${chainId}`
      ).send();

      if (!res.ok) {
        throw new Error('Failed to fetch rewards token balance');
      }

      return res.body.balance;
    },
    enabled: !!rewardsTokenAddress && !!userAddress && !!chainId,
  });

  const loading = userStakingBalanceLoading || userRewardsBalanceLoading;
  const error = userStakingBalanceError || userRewardsBalanceError;

  return {
    userStakingBalance,
    userRewardsBalance,
    loading,
    error: error?.message || null,
  };
};
